import { z } from "zod";

const permissionSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
  pivot: z
    .object({ role_id: z.number(), permission_id: z.number() })
    .optional(),
});

export const roleSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
  permissions: z.array(permissionSchema).optional(),
});

export const teamMemberSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  email: z.string().email(),
  emailVerifiedAt: z.string().datetime().nullable(),
  avatarUrl: z.string().url().nullable().optional(),
  roleId: z.number(),
  status: z.enum(["active", "inactive", "pending"]),
  createdBy: z.number().optional(),
  updatedBy: z.number().optional(),
  deletedBy: z.number().nullable().optional(),
  termsAcceptedAt: z.string().datetime().optional(),
  termsAcceptedIp: z.string().optional(),
  createdByInvitation: z.number().optional(),
  lastLoginIp: z.string().optional(),
  lastLoginAt: z.string().datetime().nullable(),
  deletedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  emailOtpExpiresAt: z.string().datetime().nullable(),
  twoFactorVerifiedAt: z.string().datetime().nullable(),
  trustedDevices: z.any().nullable(),
  role: roleSchema,
});

export const pendingInvitationSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string().email(),
  role: roleSchema,
  status: z.enum(["pending", "cancelled", "expired", "accepted", "sent"]),
  expiresAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});

export const paginatedTeamMembersResponseSchema = z.object({
  data: z.array(teamMemberSchema),
  currentPage: z.number(),
  perPage: z.number(),
  total: z.number(),
  lastPage: z.number(),
});

export const paginatedInvitationsResponseSchema = z.object({
  data: z.array(pendingInvitationSchema),
  currentPage: z.number(),
  perPage: z.number(),
  total: z.number(),
  lastPage: z.number(),
});

export const paginatedPermissionsResponseSchema = z.object({
  currentPage: z.number(),
  data: z.array(permissionSchema),
  lastPage: z.number(),
  perPage: z.number(),
  total: z.number(),
});

export const paginatedRolesResponseSchema = z.object({
  currentPage: z.number(),
  data: z.array(roleSchema),
  lastPage: z.number(),
  perPage: z.number(),
  total: z.number(),
});

export type Permission = z.infer<typeof permissionSchema>;
export type Role = z.infer<typeof roleSchema>;
export type TeamMember = z.infer<typeof teamMemberSchema>;
export type PendingInvitation = z.infer<typeof pendingInvitationSchema>;
export type PaginatedTeamMembersResponse = z.infer<
  typeof paginatedTeamMembersResponseSchema
>;
export type PaginatedInvitationsResponse = z.infer<
  typeof paginatedInvitationsResponseSchema
>;
